"use client";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "../../themed/Button";
import { If } from "../../Conditional";
import LoginModal from "../../themed/LoginModal";
import ApplyToHostImproveProfileModal from "../ApplyToHostImproveProfileModal";
import QuestionnaireModal from "~/components/questionnaire/QuestionnaireModal";
import ApplyToHostModal from "../ApplyToHostModal";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { createQueryString } from "../../events-components/EventProposals/useEventAndURLFilters";
import { getApplyToHostStepsAndBuilderData } from "./actions/ApplyToHostStepsPipelineBuilder";
import Spinner from "~/components/themed/spinner";
import { useSession } from "next-auth/react";
import { toast } from "react-toastify";
import { EventJourneyStatus, HostApplicationType } from "@prisma/client";
import { api } from "~/trpc/react";
import { DateTime } from "luxon";
import { EventBySlugOutput } from "~/types/EventTypesInferred";
import Image from "next/image";
import { cn } from "~/utils/fontsConfig";
import { CustomizableLayout } from "~/types/CustomizableLayout";

type Props = {
    type: HostApplicationType;
    event: EventBySlugOutput;
    refetch: () => Promise<void> | void;
    communityId?: string;
    isPreapprovedHost?: boolean;
    className?: React.HTMLAttributes<HTMLDivElement>["className"];
    buttonClassName?: React.HTMLAttributes<HTMLDivElement>["className"];
    customization?: CustomizableLayout;
};

const ApplyToHostV2 = ({
    type,
    event,
    refetch,
    communityId,
    isPreapprovedHost,
    className,
    buttonClassName,
    customization,
}: Props) => {
    const { data: session } = useSession();
    const pathname = usePathname();
    const searchParams = useSearchParams();

    const apiContext = api.useUtils();

    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [isOpen, setIsOpen] = useState(false);
    const [questionnaireCompletionId, setQuestionnaireCompletionId] =
        useState<string>();

    useEffect(() => {
        const shouldShowModal = searchParams?.get("applyToHost") === "true";
        setIsOpen(shouldShowModal);
    }, [searchParams]);

    const handleModalToggle = (show: boolean) => {
        // this is causing re-renders breaking in-flight queries
        // resulting in breaking Apply To Host flow when there's a questionnaire
        // updateQueryParam(show);
        setIsOpen(show);
    };

    const {
        data: builderData,
        isLoading: isLoadingBuilderData,
        fetchStatus: builderDataFetchStatus,
        refetch: refetchBuilderData,
    } = useQuery({
        queryKey: ["applyToHostSteps", event?.slug],
        queryFn: async () => getApplyToHostStepsAndBuilderData(event?.slug),
        enabled: !!event?.slug,
        refetchOnWindowFocus: false,
    });
    const steps = builderData?.steps || [];
    const currentStep = steps[currentStepIndex];

    const nextStep = useCallback(
        (
            builderData?: Awaited<
                ReturnType<typeof getApplyToHostStepsAndBuilderData>
            >,
        ) => {
            const mySteps = builderData?.steps ?? steps;

            const nextStepIndex = currentStepIndex + 1;
            if (currentStepIndex === mySteps.length - 1) {
                handleModalToggle(true);
                return;
            }
            setCurrentStepIndex(nextStepIndex);
        },
        [currentStepIndex, steps],
    );

    useEffect(() => {
        if (steps.length > 0 && currentStepIndex >= steps.length) {
            // if our current index is beyond the available steps, reset to beginning
            setCurrentStepIndex(0);
        }
    }, [steps, currentStepIndex]);

    const handleProfileUpdate = async () => {
        await refetchBuilderData();
        setIsOpen(true);
    };

    const applyToHostMutation = api.host.applyToHost.useMutation({
        onSuccess: () => {
            if (isPreapprovedHost)
                toast.success("You now have full edit access to the event!", {
                    autoClose: 1200,
                });
            else
                toast.success("Your application was sent to the River Team!", {
                    autoClose: 1200,
                });
        },
    });

    const handleHostApplication = async () => {
        toast.loading(
            isPreapprovedHost ? "Claiming..." : "Applying to Host...",
            {
                toastId: "applying-to-host",
            },
        );

        setIsOpen(false);

        const applicationData = {
            eventId: type === HostApplicationType.EVENT ? event?.id : undefined,
            communityId:
                type === HostApplicationType.COMMUNITY
                    ? communityId
                    : undefined,
            questionnaireCompletionId: questionnaireCompletionId,
        };

        await applyToHostMutation.mutateAsync(applicationData, {
            onSuccess: async () => {
                // refetch so rsvp buttons get re-rendered
                await refetch();
                await refetchBuilderData();
                // invalidate perms so potentially sidebar becomes visible
                apiContext.permissions.getUserPermissions.invalidate({
                    adminCheck: true,
                    event: { slug: event?.slug },
                    community: { fromEventSlug: event?.slug },
                });

                setIsOpen(false);
            },
            onError: (data) => {
                toast.error(data.message);
            },
            onSettled: () => {
                toast.dismiss("applying-to-host");
            },
        });
    };

    const getApplyStatus = () => {
        const userEventApplicationIds =
            builderData?.data?.userEventApplicationIds;

        if (!event) return "NONE";

        if (event.startTime) {
            const eventStartTime = DateTime.fromISO(
                event.startTime.toISOString(),
                {
                    zone: event.timezone,
                },
            );
            const currentTime = DateTime.local();

            if (
                eventStartTime < currentTime &&
                event.eventJourneyStatus === EventJourneyStatus.PUBLISHED
            ) {
                return "TOO_LATE";
            }
        }

        if (
            userEventApplicationIds?.includes(event.id) &&
            !event?.hosts.some((host) => host.user.id === session?.user?.id)
        ) {
            return "PROCESSING";
        }

        if (
            session?.user?.id &&
            event?.hosts.some((host) => host.user.id === session?.user?.id)
        ) {
            return "IS_HOST";
        }

        return "NONE";
    };
    const applyStatus = useMemo(() => {
        return getApplyStatus();
    }, [builderData?.data?.userEventApplicationIds, event, session?.user?.id]);

    const renderApplyButton = () => {
        if (isLoadingBuilderData || builderDataFetchStatus === "fetching") {
            return (
                <div className="mr-6 flex flex-row items-center">
                    <Spinner type="small" />
                </div>
            );
        } else if (applyStatus === "PROCESSING") {
            return (
                <Button className="w-full" variant="info" disabled={true}>
                    <Image
                        src="/icons/checkmark.svg"
                        alt="check"
                        width={22}
                        height={22}
                    />
                    Host Application Submitted
                </Button>
            );
        } else {
            if (event.hosts.length >= 1) return null;
            return (
                <Button
                    className={cn("!w-full", className)}
                    variant="secondary"
                    onClick={() => handleModalToggle(true)}
                >
                    <If condition={!!isPreapprovedHost}>Claim Host</If>
                    <If condition={!isPreapprovedHost}>Apply to Host</If>
                </Button>
            );
        }
    };

    return (
        <>
            <div className="w-full">{renderApplyButton()}</div>
            {isOpen && (
                <>
                    <If condition={currentStep === "LOGIN"}>
                        <LoginModal
                            title="Sign Up or Log in"
                            isOpen={true}
                            setIsOpen={(open) => {
                                if (!open) handleModalToggle(false);
                                else nextStep();
                            }}
                            description="Sign Up to Host Events!"
                            callbackUrl={`${pathname}?applyToHost=true`}
                            customization={customization}
                        />
                    </If>
                    <If condition={currentStep === "MISSING_DATA"}>
                        <ApplyToHostImproveProfileModal
                            open={true}
                            setOpen={(open) => {
                                handleModalToggle(open);
                            }}
                            requiredData={builderData?.data.hostRequiredData}
                            missingData={
                                builderData?.data?.missingUserData || []
                            }
                            customization={customization}
                            refetchUser={handleProfileUpdate}
                            user={builderData?.data?.user}
                        />
                    </If>
                    <If condition={currentStep === "ANSWER_QUESTIONNAIRE"}>
                        <QuestionnaireModal
                            isVisible={true}
                            setVisible={async (visible) => {
                                if (!visible) return handleModalToggle(false);
                            }}
                            onCompletion={async (success, completionId) => {
                                if (success && completionId) {
                                    const builderData =
                                        await refetchBuilderData();
                                    setQuestionnaireCompletionId(completionId);
                                    nextStep(builderData.data);
                                }
                            }}
                            questionnaire={
                                builderData?.data
                                    ?.communityQuestionnaireForHostApplications
                            }
                            eventId={event?.id}
                        />
                    </If>
                    <If condition={currentStep === "CONFIRM_APPLICATION"}>
                        <ApplyToHostModal
                            isPreapprovedHost={!!isPreapprovedHost}
                            setOpen={(open) => {
                                if (!open) return handleModalToggle(false);
                                nextStep();
                            }}
                            open={true}
                            type="EVENT"
                            submit={handleHostApplication}
                            user={builderData?.data?.user}
                        />
                    </If>
                </>
            )}
        </>
    );
};

export default ApplyToHostV2;
